import React, { useState, useRef, useEffect } from 'react';
import { Card, Container, Row, Table } from "reactstrap";
import { Col } from "react-bootstrap";
import { Breadcrumbs } from '../../AbstractElements';
import { useLoader } from '../../_helper/LoaderContext';
import { addPackage, masterTest, updatePackage, viewPackage } from './service/test-service';
import { toast } from 'react-toastify';
import { Link, useNavigate, useParams } from "react-router-dom";

export default function EditPackage() {
    const { id } = useParams()
    const [options, setOptions] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedTests, setSelectedTests] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [total, setTotal] = useState(0);
    const [originalOptions, setOriginalOptions] = useState([]);
    const [formData, setFormData] = useState({
        packageName: "",
        packageCode: ""
    });
    const [errors, setErrors] = useState({});
    const dropdownRef = useRef(null);
    const { setLoading } = useLoader();
    const navigate = useNavigate();

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // 1. Load master tests first
                const masterRes = await masterTest();
                if (!masterRes.data.success) throw new Error("Failed to load tests");

                const masterData = masterRes.data.data.allItems.map((item) => ({
                    name: item.name,
                    price: item.cost || item.price,
                    id: item._id
                }));
                setOriginalOptions(masterData);

                // 2. Load package details
                const packageRes = await viewPackage(id);
                if (!packageRes.data.success) throw new Error("Failed to load package");

                const data = packageRes.data.data;

                // Set form fields
                setFormData({
                    packageName: data.name || "",
                    packageCode: data.packageCode || ""
                });

                // Set selected tests
                const preSelected = data.includedTests.map(t => ({
                    id: t.testId,
                    name: t.testName,
                    price: t.testPrice
                }));

                setSelectedTests(preSelected);
                setTotal(data.price || preSelected.reduce((sum, t) => sum + t.price, 0));

                // NOW originalOptions is ready → filter out selected ones
                const remaining = masterData.filter(
                    opt => !preSelected.some(sel => sel.id === opt.id)
                );
                setOptions(remaining);

            } catch (error) {
                console.error(error);
                toast.warning("Failed to load data");
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadData();
        }
    }, [id]); // don't forget dependency

    // useEffect(() => {
    //     getMaster();
    //     getData();
    // }, [])

    // get View Lab List


    // 🔎 Handle Search
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);

        if (value.trim() === "") {
            // Show only unselected items
            const filtered = originalOptions.filter(
                opt => !selectedTests.some(sel => sel.id === opt.id)
            );
            setOptions(filtered);
            return;
        }

        const filtered = originalOptions.filter(
            opt =>
                opt.name.toLowerCase().includes(value.toLowerCase()) &&
                !selectedTests.some(sel => sel.id === opt.id)
        );

        setOptions(filtered);
    };

    const handleCheckboxSelect = (option) => {
        let updated;

        if (selectedTests.some(item => item.id === option.id)) {
            // REMOVE FROM SELECTED
            updated = selectedTests.filter(item => item.id !== option.id);
        } else {
            // ADD TO SELECTED
            updated = [...selectedTests, option];
        }

        setSelectedTests(updated);

        // Update total
        setTotal(updated.reduce((sum, item) => sum + item.price, 0));

        // Update dropdown option list (hide selected)
        const remaining = originalOptions.filter(
            opt => !updated.some(sel => sel.id === opt.id)
        );

        setOptions(remaining);
    };

    // ✅ Handle Select
    const handleSelect = (option) => {
        if (!selectedTests.some(test => test.name === option.name)) {
            const updated = [...selectedTests, option];
            setSelectedTests(updated);
            const remainingOptions = options.filter(item =>
                !updated.some(sel => sel.name === item.name)
            );
            setOptions(remainingOptions);
            setTotal(updated.reduce((sum, item) => sum + item.price, 0));
        }
        setIsOpen(false);
        setSearch("");
    };

    // ❌ Delete Test
    const handleDelete = (index) => {
        const deletedItem = selectedTests[index];
        const updated = selectedTests.filter((_, i) => i !== index);
        setSelectedTests(updated);
        setOptions([...options, deletedItem]);
        setTotal(updated.reduce((sum, item) => sum + item.price, 0));
    };

    // 📦 Handle Input Change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // 🚨 Validation Logic
    const validateForm = () => {
        const newErrors = {};
        if (!formData.packageName.trim()) newErrors.packageName = "Package name is required";
        if (!formData.packageCode.trim()) newErrors.packageCode = "Package code is required";
        if (selectedTests.length === 0) newErrors.selectedTests = "Please select at least one test";
        if (total <= 0) newErrors.total = "Total amount must be greater than zero";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // 💾 Submit Handler
    const handleSubmit = async () => {
        if (validateForm()) {
            setLoading(true)
            try {
                const payload = {
                    // packageId:id,
                    name: formData.packageName.trim(),
                    packageCode: formData.packageCode.trim(),
                    price: total,
                    includedTests: selectedTests.map(test => test.id)
                    // includedTests: selectedTests.map(test => ({
                    //     testId: test.id,
                    //     testName: test.name,
                    //     testPrice: test.price
                    // }))
                }
                const res = await updatePackage(id, payload)
                if (res.data.success == true) {
                    toast.success(res.data.message)
                    navigate(`${process.env.PUBLIC_URL}/package-list`);
                }
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false)
            }

        }
    };

    // 🧭 Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <>
            <Breadcrumbs mainTitle="Update Package" parent="Home" subParent="Package List" title="Update Package" />
            <Container fluid={true}>
                <Row>
                    <Col className="h-100">
                        <Card>
                            <div className="row p-3">
                                {/* Left Section */}
                                <div className="col-md-9">
                                    <div className="row">
                                        <div className="col-md-3">
                                            <label className='fw-bold'>Package Name</label>
                                            <input
                                                type="text"
                                                name="packageName"
                                                value={formData.packageName}
                                                onChange={handleInputChange}
                                                className={`form-control ${errors.packageName ? "is-invalid" : ""}`}
                                                placeholder='Enter Package Name'
                                            />
                                            {errors.packageName && <div className="invalid-feedback">{errors.packageName}</div>}
                                        </div>

                                        <div className="col-md-3">
                                            <label className='fw-bold'>Package Code</label>
                                            <input
                                                type="text"
                                                name="packageCode"
                                                value={formData.packageCode}
                                                onChange={handleInputChange}
                                                className={`form-control ${errors.packageCode ? "is-invalid" : ""}`}
                                                placeholder='Enter Package Code'
                                            />
                                            {errors.packageCode && <div className="invalid-feedback">{errors.packageCode}</div>}
                                        </div>

                                        {/* Dropdown Section */}
                                        <div className="col-md-6" ref={dropdownRef}>
                                            <label className="fw-bold mb-2">Test</label>
                                            <div className="position-relative">
                                                <button
                                                    type="button"
                                                    className="form-select text-start"
                                                    onClick={() => setIsOpen(!isOpen)}
                                                >
                                                    Select Test(s)
                                                </button>

                                                {isOpen && (
                                                    <div
                                                        className="position-absolute bg-white border rounded shadow-sm w-100 mt-1"
                                                        style={{ zIndex: 1050, maxHeight: "300px", overflowY: "auto" }}
                                                    >
                                                        {/* Search */}
                                                        <div className="p-2 border-bottom">
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Search..."
                                                                value={search}
                                                                onChange={handleSearch}
                                                            />
                                                        </div>

                                                        {/* Multi Select List */}
                                                        {options.length > 0 ? (
                                                            options.map((opt, index) => (
                                                                <label
                                                                    key={index}
                                                                    className="dropdown-item px-3 py-2 d-flex justify-content-between align-items-center"
                                                                    style={{ cursor: "pointer" }}
                                                                >
                                                                    <div>
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input me-2"
                                                                            checked={selectedTests.some(test => test.id === opt.id)}
                                                                            onChange={() => handleCheckboxSelect(opt)}
                                                                        />
                                                                        {opt.name}
                                                                    </div>
                                                                    <span>{opt.price}</span>
                                                                </label>
                                                            ))
                                                        ) : (
                                                            <div className="text-muted px-3 py-2">No results found</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {errors.selectedTests && (
                                                <small className="text-danger">{errors.selectedTests}</small>
                                            )}
                                        </div>

                                        {/* Table */}
                                        <div className="col-md-12 mt-3">
                                            <div className="recent-table table-responsive currency-table tw-rounded-lg">
                                                <Table className="mb-0 table-center table-bordered table-hover" style={{ tableLayout: "fixed", width: "100%" }}>
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "10%", background: '#1b255b', color: 'white' }}><b>Sr.No</b></th>
                                                            <th style={{ width: "55%", background: '#1b255b', color: 'white' }}><b>Test Name</b></th>
                                                            <th style={{ width: "20%", background: '#1b255b', color: 'white' }}><b>Price</b></th>
                                                            <th className='text-center' style={{ width: "15%", background: '#1b255b', color: 'white' }}><b>Action</b></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {selectedTests.length > 0 ? (
                                                            selectedTests.map((item, index) => (
                                                                <tr key={index}>
                                                                    <td className="text-center">{index + 1}</td>
                                                                    <td>{item.name}</td>
                                                                    <td>{item.price}</td>
                                                                    <td className="text-center">
                                                                        <i
                                                                            className='bi bi-trash text-danger cursor-pointer'
                                                                            onClick={() => handleDelete(index)}
                                                                        ></i>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <>
                                                                <tr >
                                                                    <td colSpan={4} className='tw-text-center tw-font-bold'>No Test Selected</td>
                                                                </tr>
                                                            </>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side: Total */}
                                <div className="col-md-3 mt-4">
                                    <div className="rounded bg-primary p-3 border">
                                        <label className="fw-bold">Total Amount</label>
                                        <input
                                            type="number"
                                            className={`form-control fw-bold ${errors.total ? "is-invalid" : ""}`}
                                            value={total}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setTotal(value === "" ? "" : Number(value));
                                            }}
                                        />
                                        {errors.total && <div className="invalid-feedback">{errors.total}</div>}
                                    </div>

                                    <button
                                        className="btn btn-primary mt-3 w-100"
                                        onClick={handleSubmit}
                                    >
                                        Update
                                    </button>
                                    <Link to={`${process.env.PUBLIC_URL}/package-list`}>
                                        <button
                                            className="btn btn-secondary mt-3 w-100"
                                           
                                        >
                                            Cancel
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
